// reference -> https://codesandbox.io/s/floating-ui-reusable-tooltip-component-with-arrow-46i10k?file=/src/Tooltip.tsx

import { createContext, useContext, useMemo, useState, forwardRef, isValidElement, cloneElement, useRef, ReactNode } from 'react';
import {
    Placement,
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    useMergeRefs,
    FloatingPortal,
    arrow,
} from '@floating-ui/react';

export type UseTooltipProps = {
    initialOpen?: boolean;
    placement?: Placement;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    delayMs?: number;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useTooltip({
    initialOpen = false,
    placement = 'top',
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    delayMs,
}: UseTooltipProps = {}) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
    const arrowRef = useRef(null);

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;

    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(10),
            flip({
                crossAxis: placement.includes('-'),
                fallbackAxisSideDirection: 'start',
                padding: 5,
            }),
            shift({ padding: 5 }),
            arrow({ element: arrowRef }),
        ],
    });

    const context = data.context;

    const hover = useHover(context, {
        move: false,
        delay: {
            open: delayMs,
            close: 0,
        },
        enabled: controlledOpen == null,
    });
    const focus = useFocus(context, {
        enabled: controlledOpen == null,
    });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'tooltip' });

    const interactions = useInteractions([hover, focus, dismiss, role]);

    return useMemo(
        () => ({
            open,
            setOpen,
            arrowRef,
            ...interactions,
            ...data,
        }),
        [open, setOpen, interactions, data]
    );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

export function useTooltipContext(): ContextType {
    const context = useContext(TooltipContext);

    if (context == null) {
        throw new Error('Tooltip components must be wrapped in <Tooltip />');
    }

    return context;
}

export function Tooltip({ children, ...options }: { children: React.ReactNode } & UseTooltipProps): JSX.Element {
    // This can accept any props as options, e.g. `placement` or other positioning options.
    const tooltip = useTooltip(options);
    return <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>;
}

export const TooltipTrigger = forwardRef<HTMLElement, React.HTMLProps<HTMLElement> & { children: unknown; asChild?: boolean }>(
    function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
        const context = useTooltipContext();
        // @ts-ignore
        const childrenRef = (children as unknown).ref;
        const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

        // `asChild` allows the user to pass any element as the anchor
        if (asChild && isValidElement(children)) {
            return cloneElement(
                children,
                context.getReferenceProps({
                    ref,
                    ...props,
                    ...children.props,
                    'data-state': context.open ? 'open' : 'closed',
                })
            );
        }

        return (
            <div
                ref={ref}
                // The user can style the trigger based on the state
                data-state={context.open ? 'open' : 'closed'}
                // className="w-fit"
                {...context.getReferenceProps(props)}
            >
                {children}
            </div>
        );
    }
);

export const TooltipContent = forwardRef<
    HTMLDivElement,
    React.HTMLProps<HTMLDivElement> & { children: string | string[] | { content: string; icon?: ReactNode }[] }
>(function TooltipContent({ children }, propRef) {
    const context = useTooltipContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    if (!context.open) return null;

    const { x: arrowX, y: arrowY } = context.middlewareData.arrow ?? {};

    const staticSide =
        {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
        }[context.placement.split('-')[0]] ?? '';

    return (
        <FloatingPortal>
            <div
                ref={ref}
                className="break-words"
                style={{
                    position: context.strategy,
                    top: context.y ?? 0,
                    left: context.x ?? 0,
                    visibility: context.x == null ? 'hidden' : 'visible',
                    zIndex: 999,
                    backgroundColor: '#4b5563',
                    color: 'white',
                    fontSize: 12,
                    padding: 8,
                    borderRadius: 4,
                    maxWidth: '35ch',
                }}
            >
                {Array.isArray(children) &&
                    children.map((message, idx) => {
                        return (
                            <div key={idx}>
                                {typeof message === 'string' ? (
                                    <p className="leading-5">{message}</p>
                                ) : (
                                    <div className="flex gap-2 items-center">
                                        <p className="leading-5">{message.content}</p>
                                        {message.icon}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                {typeof children === 'string' && <p>{children}</p>}
                <div
                    ref={context.arrowRef}
                    style={{
                        position: 'absolute',
                        width: '10px',
                        height: '10px',
                        background: 'inherit',
                        left: arrowX != null ? `${arrowX}px` : '',
                        top: arrowY != null ? `${arrowY}px` : '',
                        [staticSide]: '-5px',
                        transform: 'rotate(45deg)',
                    }}
                />
            </div>
        </FloatingPortal>
    );
});

export type TooltipV2Props = {
    /**
     * The content of the tooltip
     */
    message?: string | string[] | { content: string; icon?: ReactNode }[];

    /**
     * Which side of the content to display on
     */
    position?: 'top' | 'right' | 'bottom' | 'left';

    /**
     * Prevents the tooltip from showing
     */
    disabled?: boolean;

    /**
     * Delays the activation of the tooltip
     */
    delayMs?: number;

    /**
     * Content the tooltip will be wrapped around
     */
    children?: ReactNode | ReactNode[];
};

export function TooltipV2({ message, position = 'right', disabled = false, children, delayMs }: TooltipV2Props): JSX.Element {
    if (disabled) {
        return <>{children}</>;
    }

    return (
        <Tooltip placement={position} delayMs={delayMs}>
            <TooltipTrigger>{children}</TooltipTrigger>
            <TooltipContent>{message}</TooltipContent>
        </Tooltip>
    );
}
